import { useMemo } from "react"
import { createQuery } from "@nova/queryable"
import {
    computeCurrencyStatus,
    CurrencyFlight,
    CurrencyMetric,
    CurrencyState,
    summarizeCurrencyStates,
} from "@nova/pilot-currency"
import { usePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"

const policyAssignmentsQuery = createQuery({
    resource: "currency_policy_users",
    select: [
        "user_id",
        "assigned_at",
        "policy.id",
        "policy.name",
        "policy.metric",
        "policy.threshold",
        "policy.window_days",
        "policy.warning_days",
        "policy.drone_types",
    ],
})

const DAY_MS = 24 * 3600 * 1000

const buildFlightEventsQuery = (userIds: string[], windowStart: string) =>
    createQuery({
        resource: "metadata",
        select: [
            "mission.user_id",
            "started_at",
            "airtime",
            "mission.aircraft.manufacturer",
            "mission.aircraft.model",
        ],
        filters: [
            { field: "mission.user_id", op: "IN", value: userIds },
            { field: "started_at", op: ">=", value: windowStart },
        ],
    })

export type CurrencyPolicyStatus = {
    policyId: string
    policyName: string
    metric: CurrencyMetric
    threshold: number
    droneTypes: string[]
    progress: number
    state: CurrencyState
    daysLeft: number | null
}

export const summarizeCurrencyStatuses = (statuses: CurrencyPolicyStatus[]) => {
    const summary = summarizeCurrencyStates(statuses)
    const label =
        summary.state === "overdue"
            ? "Overdue"
            : summary.state === "upcoming"
              ? `${summary.daysLeft ?? 0} days left`
              : "Up to date"
    return { state: summary.state, label }
}

export const currencyStateColor: Record<CurrencyState, string> = {
    current: "text-green-200",
    upcoming: "text-orange-200",
    overdue: "text-red-200",
}

export const usePilotCurrencyStatuses = () => {
    const { data: assignments, isLoading: loadingAssignments } =
        usePilotTrackingQuery(policyAssignmentsQuery)

    const flightEventsQuery = useMemo(() => {
        const userIds = [...new Set((assignments ?? []).map((a) => a.user_id))]
        const maxWindowDays = (assignments ?? []).reduce(
            (max, a) => Math.max(max, Number(a["policy.window_days"])),
            0
        )
        const windowStart = new Date(
            Math.floor((Date.now() - maxWindowDays * DAY_MS) / DAY_MS) * DAY_MS
        ).toISOString()
        return buildFlightEventsQuery(userIds, windowStart)
    }, [assignments])

    const { data: flights, isLoading: loadingFlights } = usePilotTrackingQuery(flightEventsQuery)

    const statusesByUser = useMemo(() => {
        const map = new Map<string, CurrencyPolicyStatus[]>()
        if (!assignments || !flights) return map

        const now = Date.now()

        const flightsByUser = new Map<string, CurrencyFlight[]>()
        for (const f of flights) {
            const userId = f["mission.user_id"]
            if (!userId) continue
            const list = flightsByUser.get(userId) ?? []
            list.push({
                startedAt: new Date(f.started_at).getTime(),
                airtimeSeconds: Number(f.airtime ?? 0),
                manufacturer: f["mission.aircraft.manufacturer"] ?? null,
                model: f["mission.aircraft.model"] ?? null,
            })
            flightsByUser.set(userId, list)
        }

        for (const a of assignments) {
            const metric = a["policy.metric"] as CurrencyMetric
            const threshold = Number(a["policy.threshold"])
            const droneTypes = (a["policy.drone_types"] as string[]) ?? []
            const userFlights = flightsByUser.get(a.user_id) ?? []

            const { progress, state, daysLeft } = computeCurrencyStatus(
                {
                    metric,
                    threshold,
                    windowDays: Number(a["policy.window_days"]),
                    warningDays: Number(a["policy.warning_days"]),
                    droneTypes,
                    assignedAt: new Date(a.assigned_at).getTime(),
                },
                userFlights,
                now
            )

            const status: CurrencyPolicyStatus = {
                policyId: a["policy.id"]!,
                policyName: a["policy.name"]!,
                metric,
                threshold,
                droneTypes,
                progress,
                state,
                daysLeft,
            }

            const list = map.get(a.user_id) ?? []
            list.push(status)
            map.set(a.user_id, list)
        }

        return map
    }, [assignments, flights])

    return { statusesByUser, isLoading: loadingAssignments || loadingFlights }
}
